// frontend/src/components/blog/author/BloggerDashboard.js
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../components/AuthContext";
import "./BloggerDashboard.css";

const BloggerDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, user } = useAuth();
  const location = useLocation();

  // This will be used to trigger a refresh when the component is revisited
  const [refreshKey, setRefreshKey] = useState(0);

  // Effect to update the refresh key when the component is revisited
  useEffect(() => {
    // Increment the refresh key whenever the location changes to this component
    setRefreshKey((prevKey) => prevKey + 1);
  }, [location]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts?author=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPosts(response.data);
      } catch (err) {
        setError("Failed to load your blog posts");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [token, user.id, refreshKey]); // Add refreshKey to dependencies to trigger refetch

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "draft":
        return "status-draft";
      case "pending_approval":
        return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "published":
        return "status-published";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading)
    return <div className="blogger-loading">Loading your dashboard...</div>;
  if (error) return <div className="blogger-error">{error}</div>;

  return (
    <div className="blogger-dashboard">
      <div className="dashboard-header">
        <h1>Blogger Dashboard</h1>
        <p>Welcome, {user.firstName}! Manage your blog posts here.</p>
        <div className="dashboard-actions">
          <Link to="/profile" className="profile-btn">
            Edit Profile
          </Link>
          <button
            className="refresh-btn"
            onClick={() => setRefreshKey((prevKey) => prevKey + 1)}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh Posts"}
          </button>
          <Link to="/staff/blog/create" className="create-post-btn">
            Create New Post
          </Link>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-value">{posts.length}</div>
          <div className="stat-label">Total Posts</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {posts.filter((post) => post.status === "published").length}
          </div>
          <div className="stat-label">Published</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {posts.filter((post) => post.status === "draft").length}
          </div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {posts.filter((post) => post.status === "pending_approval").length}
          </div>
          <div className="stat-label">Pending Approval</div>
        </div>
      </div>

      <div className="dashboard-content">
        <h2>Your Blog Posts</h2>

        {posts.length === 0 ? (
          <div className="no-posts">
            <p>You haven't created any blog posts yet.</p>
            <Link to="/staff/blog/create" className="create-first-post-btn">
              Create Your First Post
            </Link>
          </div>
        ) : (
          <div className="posts-table-container">
            <table className="posts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post._id}>
                    <td>{post.title}</td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadgeClass(
                          post.status
                        )}`}
                      >
                        {post.status.replace("_", " ")}
                      </span>
                    </td>
                    <td>{formatDate(post.createdAt)}</td>
                    <td>{formatDate(post.updatedAt)}</td>
                    <td className="post-actions">
                      {post.status === "published" && (
                        <Link to={`/blog/${post.slug}`} className="view-btn">
                          View
                        </Link>
                      )}

                      <Link
                        to={`/staff/blog/edit/${post.slug}`}
                        className="edit-btn"
                      >
                        Edit
                      </Link>

                      {post.status === "draft" && (
                        <button
                          className="submit-btn"
                          onClick={async () => {
                            try {
                              await axios.post(
                                `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${post._id}/submit`,
                                {},
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                }
                              );
                              // Update the post status in the local state
                              setPosts(
                                posts.map((p) =>
                                  p._id === post._id
                                    ? { ...p, status: "pending_approval" }
                                    : p
                                )
                              );
                            } catch (err) {
                              console.error("Failed to submit post:", err);
                              alert("Failed to submit post for approval");
                            }
                          }}
                        >
                          Submit for Approval
                        </button>
                      )}

                      {post.status === "rejected" && post.rejectionReason && (
                        <div className="rejection-reason">
                          <p>Rejection reason: {post.rejectionReason}</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BloggerDashboard;
